/**
 * R4 — Páginas legais (Política de Privacidade e Termos de Uso).
 *
 * Renderiza o markdown em `src/content/legal/*.md` como conteúdo público.
 * Os .md são a fonte única de verdade do texto legal — o componente é só
 * o renderizador. Quando o advogado entregar a versão revisada, basta
 * substituir o .md; nenhum código React precisa mudar.
 *
 * Decisões deliberadas:
 *
 * - O bloco `> RASCUNHO — REVISÃO JURÍDICA…` no topo é uma instrução de
 *   processo interna e NÃO entra na página pública. Cortamos tudo antes do
 *   primeiro `#` de título.
 * - O parágrafo final em itálico (`*Documento de rascunho…*`) também é
 *   instrução interna. Cortamos tudo a partir do separador `---` final que
 *   o antecede.
 * - Tabelas (GFM) são obrigatórias — a Política tem tabelas de bases legais
 *   e operadores. Daí `remark-gfm`.
 * - Sem HTML cru: react-markdown 9 não habilita HTML por padrão; não passamos
 *   `rehype-raw`. Garante sanitização.
 * - Links internos de domínio (meunexoia.com.br/...) viram navegação SPA via
 *   react-router; demais links abrem normalmente.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSlug from "rehype-slug";
import { ArrowLeft, ArrowUp, ChevronDown } from "lucide-react";

import politicaPrivacidadeMd from "@/content/legal/politica-privacidade.md";
import termosDeUsoMd from "@/content/legal/termos-de-uso.md";
import { stripDraftBlocks } from "@/utils/legal";

// FaseC C2: slug do GitHub que o rehype-slug usa (alinhamento de anchors
// gerados automaticamente pelos headings com os hrefs do TOC).
function _slugify(text) {
  return text
    .toLowerCase()
    .trim()
    .normalize("NFD").replace(/[̀-ͯ]/g, "") // strip accents
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

const DOCS = {
  privacidade: {
    title: "Política de Privacidade",
    raw: politicaPrivacidadeMd,
    testId: "legal-page-privacidade",
  },
  termos: {
    title: "Termos de Uso",
    raw: termosDeUsoMd,
    testId: "legal-page-termos",
  },
};

// Domínio do produto — links que apontam pra cá viram navegação SPA.
const APP_HOSTS = new Set(["meunexoia.com.br", "www.meunexoia.com.br"]);

/**
 * Componente <a> customizado: links internos do domínio do produto viram
 * <Link> do react-router (navegação SPA); externos abrem em nova aba com
 * rel de segurança.
 */
function makeLinkRenderer(navigate) {
  return function LegalLink({ href, children, ...rest }) {
    if (!href) return <a {...rest}>{children}</a>;
    let internalPath = null;
    if (href.startsWith("/")) {
      internalPath = href;
    } else {
      try {
        const u = new URL(href);
        if (APP_HOSTS.has(u.host)) internalPath = u.pathname + u.search + u.hash;
      } catch {
        /* href relativo ou inválido — trata como externo */
      }
    }
    if (internalPath) {
      return (
        <Link
          to={internalPath}
          onClick={(e) => {
            e.preventDefault();
            navigate(internalPath);
          }}
          className="text-brand-cyan hover:underline"
        >
          {children}
        </Link>
      );
    }
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-brand-cyan hover:underline"
      >
        {children}
      </a>
    );
  };
}

export default function LegalPage({ slug }) {
  const navigate = useNavigate();
  const doc = DOCS[slug];
  const body = useMemo(() => (doc ? stripDraftBlocks(doc.raw) : ""), [doc]);
  const LinkRenderer = useMemo(() => makeLinkRenderer(navigate), [navigate]);

  // FaseC C2: extrair h2 do markdown pra TOC (sidebar desktop +
  // collapsible mobile). rehype-slug gera os anchors automaticamente
  // a partir do mesmo texto; _slugify mantém o link alinhado.
  const headings = useMemo(() => {
    return [...body.matchAll(/^## (.+)$/gm)].map((m) => ({
      text: m[1].trim(),
      slug: _slugify(m[1].trim()),
    }));
  }, [body]);

  // Back-to-top: aparece depois de 2000px de scroll. Smooth scroll na volta.
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 2000);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!doc) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-surface-canvas text-foreground-muted"
        data-testid="legal-page-unknown"
      >
        Documento não encontrado.
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-surface-canvas text-foreground"
      data-testid={doc.testId}
    >
      {/* Barra superior — logo + voltar. Acessível sem auth; o "voltar"
          é navegação interna padrão. */}
      <header className="border-b border-subtle">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/logo-nav.png"
              alt="Nexo"
              className="w-8 h-8 rounded-md object-cover"
            />
            <span className="font-display text-lg font-bold tracking-wide">
              Nexo
            </span>
          </Link>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-caption text-foreground-subtle hover:text-foreground transition-colors"
            data-testid="legal-page-back"
          >
            <ArrowLeft className="w-4 h-4" /> Voltar
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12 md:flex md:gap-10">
        {/* FaseC C2: TOC sticky em desktop (≥md). */}
        {headings.length > 0 && (
          <aside className="hidden md:block md:w-56 shrink-0" aria-label="Conteúdo">
            <div className="sticky top-8">
              <h2 className="text-micro uppercase tracking-wider text-foreground-subtle mb-3">
                Conteúdo
              </h2>
              <nav className="space-y-1" data-testid="legal-toc-desktop">
                {headings.map((h) => (
                  <a
                    key={h.slug}
                    href={`#${h.slug}`}
                    className="block text-caption text-foreground-muted hover:text-brand-cyan py-1 transition-colors"
                  >
                    {h.text}
                  </a>
                ))}
              </nav>
            </div>
          </aside>
        )}

        <div className="flex-1 min-w-0">
          {/* FaseC C2: TOC collapsible em mobile (<md). */}
          {headings.length > 0 && (
            <details
              className="md:hidden mb-6 rounded-xl bg-white/[0.04] border border-subtle"
              open={tocOpen}
              onToggle={(e) => setTocOpen(e.currentTarget.open)}
              data-testid="legal-toc-mobile"
            >
              <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between text-sm font-medium text-foreground">
                <span>Conteúdo</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${tocOpen ? "rotate-180" : ""}`} aria-hidden="true" />
              </summary>
              <nav className="px-4 pb-3 space-y-1">
                {headings.map((h) => (
                  <a
                    key={h.slug}
                    href={`#${h.slug}`}
                    onClick={() => setTocOpen(false)}
                    className="block text-caption text-foreground-muted hover:text-brand-cyan py-1 transition-colors"
                  >
                    {h.text}
                  </a>
                ))}
              </nav>
            </details>
          )}

          <article
            className="legal-prose"
            data-testid="legal-page-content"
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeSlug]}
              components={{ a: LinkRenderer }}
            >
              {body}
            </ReactMarkdown>
          </article>

          <nav
            className="mt-12 pt-6 border-t border-subtle flex flex-wrap gap-4 text-caption text-foreground-subtle"
            aria-label="Documentos legais"
          >
            {slug !== "privacidade" && (
              <Link to="/privacidade" className="hover:text-brand-cyan transition-colors">
                Política de Privacidade
              </Link>
            )}
            {slug !== "termos" && (
              <Link to="/termos" className="hover:text-brand-cyan transition-colors">
                Termos de Uso
              </Link>
            )}
          </nav>
        </div>
      </main>

      {/* FaseC C2: back-to-top depois de 2000px. safe-area-inset preserva
          home indicator no iOS. */}
      {showBackToTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Voltar ao topo"
          data-testid="legal-back-to-top"
          className="fixed bottom-[calc(env(safe-area-inset-bottom,0px)+1rem)] right-4 z-30 p-3 rounded-full bg-brand-cyan/20 border border-brand-cyan/40 backdrop-blur-md shadow-lg hover:bg-brand-cyan/30 transition-colors"
        >
          <ArrowUp className="w-5 h-5 text-brand-cyan" />
        </button>
      )}
    </div>
  );
}
